import React, { useState, useEffect ,useRef } from 'react';
import { useSearchParams } from 'react-router-dom';


import { useAuth } from '../context/AuthContext';
import { isSuperAdmin, isClinicAdmin, hasAnyPermission } from '../utils/permissions';
// Permissions come from backend - check user.permissions array
import { formatDate } from '../utils/dateUtils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle , DialogFooter} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'react-toastify';
import { Search, RotateCw, Trash2, Tag, Image as ImageIcon } from 'lucide-react';
import { useAllPhotos , usedetailphotodata , useVisitTypes , usedeleteApi , usegalleryUpdate } from '@/hooks/useApi';
import { useDebounce } from '@/hooks/useDebounce';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Trash , X} from 'lucide-react';



const Gallery = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentUser } = useAuth();
  
  const [searchQuery, setSearchQuery] = useState('');
  const [visitTypeId, setVisitTypeId] = useState('');
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [editMode, setEditMode] = useState(false);
const [editData, setEditData] = useState({
  tags: [],
  tagInput: '',
  notes: '',
});

  const loadMoreRef = useRef(null);
  const [deleteTarget, setDeleteTarget] = useState(null);


  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  // Build params object for API call
  const filterParams = {
    search: debouncedSearchQuery,
    visit_type_id: visitTypeId || undefined,
  };

  const { data: photosData, isLoading: isLoadingPhotos ,fetchNextPage, hasNextPage,isFetchingNextPage, } = useAllPhotos(filterParams);
  const photos =
  photosData?.pages?.flatMap(page => page?.data || []) || [];



  useEffect(() => {
    if (!loadMoreRef.current || !hasNextPage) return;
  
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          fetchNextPage();
        }
      },
      { threshold: 1 }
    );
  
    observer.observe(loadMoreRef.current);
  
    return () => observer.disconnect();
  }, [fetchNextPage, hasNextPage]);
  


  // Use photo_id if available, fallback to id
  const photoId = selectedPhoto?.photo_id || selectedPhoto?.id;
  const patientId = selectedPhoto?.patient_id;
  const { data: detailPhotoData, isLoading: isLoadingDetailPhotoData } = usedetailphotodata(photoId, patientId);

  const { data: visitTypesData, isLoading: isLoadingVisitTypes } = useVisitTypes();
  const visitTypes = visitTypesData?.data || [];
   const { mutate: deletePhoto, isPending: isDeleting } = usedeleteApi();
  // Debug logging
  useEffect(() => {
    if (photoId) {
      console.log('Photo ID changed, fetching details for:', photoId);
    }
  }, [photoId]);
  
  // API returns data as an array, get first item
  const photoDetail = Array.isArray(detailPhotoData?.data) 
    ? detailPhotoData?.data?.[0] || null 
    : detailPhotoData?.data || null;

  useEffect(() => {
    const photoId = searchParams.get('photo');
    if (photoId && photos.length > 0) {
      const photo = photos.find(p => String(p.photo_id || p.id) === String(photoId));
      if (photo) {
        openPhotoViewer(photo);
      }
    }
  }, [searchParams, photos]);

  const { mutate: updateGallery , isPending: isUpdatingGallery } = usegalleryUpdate();
 



  const openPhotoViewer = (photo) => {
    console.log('Opening photo viewer:', photo);
    console.log('Photo ID:', photo?.photo_id || photo?.id);
    setSelectedPhoto(photo);
    setRotation(0);
    setIsViewerOpen(true);
    setEditMode(false);
  };

  // Update editData when photoDetail is loaded
  useEffect(() => {
    if (photoDetail) {
      setEditData({
  tags: Array.isArray(photoDetail?.tags)
    ? photoDetail.tags.map(t => (typeof t === 'string' ? t : t?.name || t))
    : [],
  tagInput: '',
  notes: photoDetail?.notes || '',
});

    }
  }, [photoDetail]);

  const closePhotoViewer = () => {
    setIsViewerOpen(false);
    setSelectedPhoto(null);
    setRotation(0);
    setEditMode(false);
    setSearchParams({});
  };

  const handleRotate = () => {
    setRotation((prev) => (prev + 90) % 360);
  };

  const handleDelete = () => {
   
  };

  const handleSaveEdit = () => {
    
    if (!selectedPhoto) return;

    const payload = {
      photo_id: selectedPhoto.photo_id,
      tags: editData.tags,
      notes: editData.notes,
    };
    updateGallery(payload, {
      onSuccess: () => {
        toast.success('Photo details updated successfully');
        setEditMode(false);
      },
      onError: () => {
        toast.error('Failed to update photo details');
      },
    });
  };

  const addEditTag = (value) => {
  const tag = value.trim();
  if (!tag) return;

  setEditData(prev => {
    if (prev.tags.includes(tag)) return prev; // avoid duplicates
    return {
      ...prev,
      tags: [...prev.tags, tag],
      tagInput: '',
    };
  });
};

const removeEditTag = (tagToRemove) => {
  setEditData(prev => ({
    ...prev,
    tags: prev.tags.filter(tag => tag !== tagToRemove),
  }));
};


  const cleanUrl = (url = '') => url.replace(/\\/g, '');
  return (
    <div className="space-y-6 animate-fadeIn">
      {/* Header */}
   
      {/* Filters */}
      <Card>
        <CardContent className="p-6">
          <div className="space-y-4">
            <div className="grid grid-cols-1  lg:grid-cols-3 gap-4">
              {/* Search */}
              <div className="md:col-span-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    placeholder="Search by tags, notes, or diagnosis..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>

              {/* Visit Type Filter */}
              <div className="">
                <Select
                  value={visitTypeId || 'all'}
                  onValueChange={(value) => setVisitTypeId(value === 'all' ? '' : value)}
                  disabled={isLoadingVisitTypes}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder={isLoadingVisitTypes ? "Loading..." : "All visit types"} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Visit Types</SelectItem>
                    {isLoadingVisitTypes ? (
                      <SelectItem value="loading" disabled>Loading...</SelectItem>
                    ) : visitTypes.length > 0 ? (
                      visitTypes.map((type) => (
                        <SelectItem key={type.id} value={String(type.id)}>{type.name}</SelectItem>
                      ))
                    ) : (
                      <SelectItem value="no-types" disabled>No visit types available</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>

            

               {/* Results Count */}
            <div className="flex items-center justify-between  ">
              <p className="text-gray-600 text-sm">
                {isLoadingPhotos ? (
                  <span className="text-gray-400">Loading photos...</span>
                ) : (
                  <span>{photos.length} photo{photos.length !== 1 ? 's' : ''} found</span>
                )}
              </p>
            </div>
            </div>

           
          </div>
        </CardContent>
      </Card>

      {/* Photo Grid */}
      {photos.length === 0 ? (
        
        <Card>
          <CardContent className="p-12">
            <div className="text-center">
              <ImageIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No photos found</h3>
              <p className="text-gray-600">Try adjusting your filters or capture new photos</p>
            </div>
          </CardContent>
        </Card>
      ) : (

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5  gap-4">
          {photos.map((photo, index) => (
            <div
              key={photo.photo_id || photo.id}
              className="group relative aspect-square rounded-xl  overflow-hidden cursor-pointer hover:shadow-xl transition-all duration-300 animate-pop"
              style={{ animationDelay: `${index * 0.03}s` }}
              onClick={() => openPhotoViewer(photo)}
            >
              <img
                src={cleanUrl(photo.thumbnail_url)}
                alt="Patient photo"
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
              />
             <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent">

                <div className="absolute bottom-0 left-0 right-0 p-3">
                  <p className="text-white text-sm font-medium truncate">{(photo.patient_name)}</p>
                  <p className="text-white/80 text-xs">{photo.visit_type}</p>
                  <p className="text-white/60 text-xs">{formatDate(photo.captured_at)}</p>
                </div>
                <div>
                  <Trash
                        className="w-6 h-6 text-red-600 absolute bottom-2 right-2 bg-white rounded p-1 cursor-pointer"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteTarget({ id: photo.photo_id, type: 'photo' });
                        }}
                      />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

<Dialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
                        <DialogContent
                          className="sm:max-w-md"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <h2 className="text-lg font-semibold">Confirm Delete</h2>

                          <p className="text-sm text-gray-600 mt-2">
                            Are you sure you want to delete this photo? This action cannot be undone.
                          </p>

                          <DialogFooter>
                            <Button
                              variant="outline"
                              onClick={(e) => {
                                e.stopPropagation();
                                setDeleteTarget(null);
                              }}
                            >
                              Cancel
                            </Button>

                            <Button
  variant="destructive"
  disabled={isDeleting}
  onClick={(e) => {
    e.stopPropagation();
    deletePhoto(deleteTarget, {
      onSuccess: () => {
        toast.success('Photo deleted successfully');
        setDeleteTarget(null);
      },
      onError: () => {
        toast.error('Failed to delete photo');
      },
    });
  }}
>
  {isDeleting ? 'Deleting...' : 'Delete'}
</Button>

                          </DialogFooter>
                        </DialogContent>
                      </Dialog>

<div ref={loadMoreRef} className="h-10 flex justify-center items-center">
  {isFetchingNextPage && (
    <p className="text-sm text-gray-500">Loading more photos...</p>
  )}
</div>


      {/* Photo Viewer Dialog */}
      <Dialog open={isViewerOpen} onOpenChange={closePhotoViewer}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden">
          {selectedPhoto && (
            <>
              <DialogHeader>
                <DialogTitle>Photo Details</DialogTitle>
              </DialogHeader>
              {isLoadingDetailPhotoData ? (
                <div className="flex items-center justify-center h-96">
                  <div className="text-center">
                    <div className="w-16 h-16 border-4 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading photo details...</p>
                  </div>
                </div>
              ) : photoDetail ? (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 overflow-y-auto max-h-[75vh]">
                {/* Photo Preview */}
                <div className="lg:col-span-2 space-y-4">
                  <div className="bg-gray-100 rounded-lg overflow-hidden flex items-center justify-center min-h-[400px]">
                    <img
                      src={cleanUrl(photoDetail.original_url)}
                      alt="Patient photo"
                      className="max-w-full max-h-[600px] object-contain transition-transform duration-300"
                      style={{ transform: `rotate(${rotation}deg)` }}
                    />
                  </div>
                  
                  {/* Actions */}
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={handleRotate}>
                      <RotateCw className="w-4 h-4 mr-2" />
                      Rotate
                    </Button>
                    {hasAnyPermission(currentUser, ['patient.update','all']) && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setEditMode(!editMode)}
                      className="text-primary-600 hover:text-primary-700"
                    >
                      <Tag className="w-4 h-4 mr-2" />
                      {editMode ? 'Cancel Edit' : 'Edit Tags & Notes'}
                    </Button>
                    )}
                   
                  </div>
                </div>

                {/* Photo Info */}
                <div className="space-y-4">
                  <div>
                    <Label className="text-sm text-gray-500">Patient</Label>
                    <p className="font-medium text-gray-900">{photoDetail?.patient_name || 'N/A'}</p>
                  </div>
                  <div>
                    <Label className="text-sm text-gray-500 mr-2">Visit Type</Label>
                    <Badge className="mt-1 bg-primary-100 text-primary-700 hover:bg-primary-100">
                      {photoDetail?.visit_type || 'N/A'}
                    </Badge>
                  </div>
                  {photoDetail?.diagnosis && (
                    <div>
                      <Label className="text-sm text-gray-500">Diagnosis</Label>
                      <p className="font-medium text-gray-900">{photoDetail.diagnosis}</p>
                    </div>
                  )}
                  <div>
                    <Label className="text-sm text-gray-500">Captured On</Label>
                    <p className="font-medium text-gray-900">{formatDate(photoDetail?.captured_at)}</p>
                  </div>
                  {photoDetail?.treatment_plan && (
                    <div>
                      <Label className="text-sm text-gray-500">Treatment Plan</Label>
                      <p className="font-medium text-gray-900">{photoDetail.treatment_plan}</p>
                    </div>
                  )}

                  {editMode ? (
                    <>
                      <div>
  <Label className="text-sm text-gray-500 mb-2">Tags</Label>

  {/* Existing tags */}
  <div className="flex flex-wrap gap-2 mb-2">
    {editData.tags.map((tag, index) => (
      <span
        key={index}
        className="flex items-center gap-1 bg-red-100 text-red-700 px-3 py-1 rounded-full text-sm"
      >
        {tag}
        <button
          type="button"
          onClick={() => removeEditTag(tag)}
          className="hover:text-red-900"
        >
          <X className="w-3 h-3" />
        </button>
      </span>
    ))}
  </div>

  {/* Tag input */}
  <Input
    placeholder="Type tag and press comma or Enter"
    value={editData.tagInput}
    onChange={(e) =>
      setEditData(prev => ({ ...prev, tagInput: e.target.value }))
    }
    onKeyDown={(e) => {
      if (e.key === ',' || e.key === 'Enter') {
        e.preventDefault();
        addEditTag(editData.tagInput);
      }
    }}
    onBlur={() => addEditTag(editData.tagInput)}
  />

  <p className="text-xs text-gray-500 mt-1">
    Press comma, Enter, or click outside to add a tag
  </p>
</div>

                      <div>
                        <Label className="text-sm text-gray-500 mb-2">Notes</Label>
                        <Textarea
                          placeholder="Add notes"
                          value={editData.notes || ''}
                          onChange={(e) => setEditData({ ...editData, notes: e.target.value })}
                          rows={4}
                        />
                      </div>
                      <Button
                        onClick={handleSaveEdit}
                        className="w-full bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white"
                      >
                        Save Changes
                      </Button>
                    </>
                  ) : (
                    <>
                      <div>
                        <Label className="text-sm text-gray-500 mb-2">Tags</Label>
                        <div className="flex flex-wrap gap-2 ">
                          {photoDetail?.tags && Array.isArray(photoDetail.tags) && photoDetail.tags.length > 0 ? (
                            photoDetail.tags.map((tag, index) => (
                              <Badge key={index} variant="outline" className="bg-red-100 text-red-800">
                                {typeof tag === 'string' ? tag : tag?.name || tag}
                              </Badge>
                            ))
                          ) : (
                            <p className="text-sm text-gray-500">No tags</p>
                          )}
                        </div>
                      </div>
                      <div>
                        <Label className="text-sm text-gray-500 mb-2">Notes</Label>
                        <p className="text-sm text-gray-700">{photoDetail?.notes || 'No notes'}</p>
                      </div>
                    </>
                  )}
                </div>
              </div>
              ) : (
                <div className="text-center p-8">
                  <p className="text-gray-600">Photo details not available</p>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Gallery;
